import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Gift, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";

const formatDateEs = (iso) => {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

/** Saldo de mascarillas del instructor y apunte de las de cortesía
 *  (familiares, amigos, personal que no es alumno). */
export const CourtesyMasks = ({ data, onChanged }) => {
  const [qty, setQty] = useState("1");
  const [saving, setSaving] = useState(false);
  const remaining = data?.remaining ?? 0;
  const entries = data?.courtesy || [];

  const add = async () => {
    setSaving(true);
    try {
      await adminApi.post("/instructor/masks/courtesy", { qty: parseInt(qty, 10) });
      toast.success(`Apuntadas ${qty} mascarilla${qty === "1" ? "" : "s"} de cortesía.`);
      setQty("1");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo apuntar.");
    } finally {
      setSaving(false);
    }
  };

  const undo = async (id) => {
    try {
      await adminApi.delete(`/instructor/masks/courtesy/${id}`);
      toast.success("Apunte borrado.");
      onChanged();
    } catch (e) {
      toast.error("No se pudo borrar el apunte.");
    }
  };

  return (
    <div className="rounded-md border border-zinc-200 p-4 space-y-3" data-testid="instructor-masks-courtesy">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm text-zinc-700">
          Te deben quedar{" "}
          <span
            className={`font-mono text-2xl font-bold ${remaining <= 0 ? "text-red-700" : "text-zinc-950"}`}
            data-testid="instructor-masks-remaining"
          >
            {remaining}
          </span>{" "}
          mascarillas
        </div>
        <div className="text-[11px] text-zinc-500">
          Recibidas {data?.received_total ?? 0} · alumnos {data?.delivered_estimate ?? 0} · cortesía{" "}
          {data?.courtesy_total ?? 0}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-800">
          <Gift className="h-4 w-4" /> De cortesía (no alumnos):
        </span>
        <Select value={qty} onValueChange={setQty}>
          <SelectTrigger className="h-10 w-20" data-testid="instructor-masks-courtesy-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["1", "2", "3", "4", "5"].map((n) => (
              <SelectItem key={n} value={n} data-testid={`instructor-masks-courtesy-option-${n}`}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          onClick={add}
          disabled={saving}
          className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold"
          data-testid="instructor-masks-courtesy-add"
        >
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Apuntar
        </Button>
      </div>

      {entries.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-testid="instructor-masks-courtesy-list">
          {entries.map((c) => (
            <li
              key={c.id}
              className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 pl-3 pr-1 py-0.5 text-xs"
              data-testid={`instructor-masks-courtesy-${c.id}`}
            >
              <span className="font-mono">{formatDateEs(c.date)}</span>
              <span className="font-bold">{c.qty} uds</span>
              <button
                type="button"
                onClick={() => undo(c.id)}
                className="p-1 rounded-full text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900 transition-colors"
                title="Borrar este apunte"
                data-testid={`instructor-masks-courtesy-undo-${c.id}`}
              >
                <Undo2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
